import React, { useState, useEffect } from "react";
import { formatPhone } from "../../utils/formatPhone";
import { useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import secureAxios from "../../utils/secureAxios";
import UserRateHistory from "./UserRateHistory";

const EditUserForm = ({ user, onFinish }) => {
  const [formData, setFormData] = useState({
    name: user.name || "",
    email: user.email || "",
    phone_number: formatPhone(user.phone_number || ""),
    status_id: user.status_id || "",
    address_line_1: user.address?.address_line_1 || "",
    address_line_2: user.address?.address_line_2 || "",
    city: user.address?.city || "",
    postal_code: user.address?.postal_code || "",
    state_id: user.address?.state_id || "",
    role_id: user.role_id || "",
    hourly_rate: parseFloat(user.hourly_rate ?? 0),
    mileage_rate: parseFloat(user.mileage_rate ?? 0),
    tenant_id: user.tenant_id || "",
    has_placement_access: user.has_placement_access || false,
  });

  const [states, setStates] = useState([]);
  const [message, setMessage] = useState("");
  const [statuses, setStatuses] = useState([]);
  const [roles, setRoles] = useState([]);
  const [tenantName, setTenantName] = useState("");
  const [dataChangeAcknowledged, setDataChangeAcknowledged] = useState(false);
  const [showRoleChangeWarning, setShowRoleChangeWarning] = useState(false);
  const [showRateHistory, setShowRateHistory] = useState(false);

  const { user: currentUser } = useContext(AuthContext);
  const isPrimaryTenant = currentUser?.is_primary === true;
  const isAdmin = currentUser?.role === "admin";
  const tenant_id = currentUser?.tenant_id || "";

  useEffect(() => {
    if (user) {
      secureAxios
        .get(`/api/users/${user.user_id}`)
        .then((res) => {
          setFormData((prev) => ({
            ...prev,
            role_id: res.data.role_id || "",
            hourly_rate: parseFloat(res.data.hourly_rate) || 0,
            mileage_rate: parseFloat(res.data.mileage_rate) || 0,
            has_placement_access: res.data.has_placement_access || false,
          }));
        })
        .catch((err) => {
          console.error("âŒ Failed to load user details:", err);
          if (err.response?.status === 401) {
            setMessage("âŒ Unauthorized. Please log in again.");
          }
        });
    }
  }, [user]);

  useEffect(() => {
    if (isPrimaryTenant && formData.tenant_id) {
      secureAxios
        .get(`/api/tenants/${formData.tenant_id}`)
        .then((res) => {
          setTenantName(res.data.tenant_name || "");
        })
        .catch((err) => {
          console.error("âŒ Failed to load tenant name:", err);
          setTenantName("");
        });
    }
  }, [formData.tenant_id, isPrimaryTenant]);

  useEffect(() => {
    // Load states (GLOBAL â€” no tenant_id needed)
    secureAxios
      .get("/api/states")
      .then((res) => {
        setStates(res.data);
      })
      .catch((err) => console.error("âŒ Failed to load states:", err));

    // Load statuses (GLOBAL - no tenant_id needed)
    secureAxios
      .get("/api/status")
      .then((res) => {
        setStatuses(res.data);
        console.log("âœ… Statuses loaded:", res.data);
      })
      .catch((err) => console.error("âŒ Failed to load statuses:", err));

    // Load roles (Check if tenant-specific or global)
    secureAxios
      .get("/api/roles")
      .then((res) => {
        setRoles(res.data);
        console.log("âœ… Roles loaded:", res.data);
      })
      .catch((err) => console.error("âŒ Failed to load roles:", err));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Check if role is being changed
    if (name === "role_id" && value !== user.role_id) {
      setShowRoleChangeWarning(true);
    }

    if (name === "phone_number") {
      const formatted = formatPhone(value);
      setFormData((prev) => ({
        ...prev,
        phone_number: formatted,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!dataChangeAcknowledged) {
      setMessage(
        "âŒ Please acknowledge the data modification notice before proceeding."
      );
      return;
    }

    setMessage("Saving...");

    const cleanedPhone = formData.phone_number.replace(/\D/g, "");
    const hourlyRate = parseFloat(formData.hourly_rate);
    const mileageRate = parseFloat(formData.mileage_rate);

    if (hourlyRate < 0) {
      setMessage("âŒ Hourly rate cannot be negative.");
      return;
    }

    if (mileageRate < 0) {
      setMessage("âŒ Mileage rate cannot be negative.");
      return;
    }

    let latitude = null;
    let longitude = null;

    try {
      // Lookup state code
      const state = states.find((s) => s.state_id === formData.state_id);
      const stateCode = state?.state_code || "";
      const fullAddress = `${formData.address_line_1}, ${formData.city}, ${stateCode} ${formData.postal_code}`;

      // Geocode using backend route (backend handles the actual geocoding)
      const geoRes = await secureAxios.get("/api/geocode", {
        params: { q: fullAddress },
      });

      if (geoRes.data && geoRes.data.length > 0) {
        latitude = geoRes.data[0].lat;
        longitude = geoRes.data[0].lon;
      }
    } catch (geoErr) {
      console.warn("âš ï¸ Geocoding failed, saving without coordinates.");
    }

    try {
      await secureAxios.put(`/api/users/${user.user_id}`, {
        ...formData,
        tenant_id: formData.tenant_id,
        phone_number: cleanedPhone,
        status_id: formData.status_id,
        role_id: formData.role_id,
        hourly_rate: hourlyRate || 0,
        mileage_rate: mileageRate || 0,
        latitude,
        longitude,
        modification_acknowledged: dataChangeAcknowledged,
        modification_acknowledged_at: new Date().toISOString(),
        modified_by: currentUser?.user_id,
      });

      setMessage("âœ… User updated!");
      setTimeout(() => {
        setMessage("");
        onFinish();
      }, 1000);
    } catch (err) {
      console.error("âŒ Error updating user:", err);
      if (err.response?.status === 401) {
        setMessage("âŒ Unauthorized. Please log in again.");
      } else if (err.response?.status === 403) {
        setMessage("âŒ You don't have permission to update this user.");
      } else {
        setMessage("âŒ Error updating user.");
      }
    }
  };

  return (
    <div>
      <h2>Edit User</h2>

      {/* HIPAA Modification Notice */}
      <div className="alert alert-warning mb-4">
        <h5 className="alert-heading">
          <i className="bi bi-exclamation-triangle me-2"></i>
          HIPAA Compliance Notice - Data Modification
        </h5>
        <p className="mb-2">
          You are modifying Protected Health Information (PHI) and Personally
          Identifiable Information (PII). Please ensure:
        </p>
        <ul className="mb-2">
          <li>
            You have a legitimate business need to modify this user's
            information
          </li>
          <li>All changes comply with the minimum necessary standard</li>
          <li>You have verified the accuracy of any updated information</li>
          <li>
            All modifications will be logged in the audit trail with your user
            ID
          </li>
        </ul>
        <small className="text-muted">
          Unauthorized modifications may result in disciplinary action and legal
          penalties under HIPAA.
        </small>
      </div>

      {/* Role Change Warning */}
      {showRoleChangeWarning && (
        <div className="alert alert-danger mb-3">
          <h6 className="alert-heading">
            <i className="bi bi-shield-exclamation me-2"></i>
            Role Change Warning
          </h6>
          <p className="mb-0">
            Changing a user's role will immediately affect their system access
            permissions. Ensure this change has been properly authorized and
            documented.
          </p>
        </div>
      )}

      {message && (
        <div
          className={`alert ${
            message.includes("âœ…") ? "alert-success" : "alert-danger"
          } alert-dismissible`}
        >
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="row g-3">
        <div className="col-md-6">
          <label className="form-label">
            Name <span className="text-danger">*</span>
          </label>
          <input
            name="name"
            className="form-control"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="col-md-6">
          <label className="form-label">
            Email <span className="text-danger">*</span>
          </label>
          <input
            name="email"
            className="form-control"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
          <small className="text-muted">
            Changing email will affect user login credentials
          </small>
        </div>

        <div className="col-md-6">
          <label className="form-label">
            Status <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            name="status_id"
            value={formData.status_id}
            onChange={handleChange}
            required
          >
            <option value="">Select Status</option>
            {statuses.map((s) => (
              <option key={s.status_id} value={s.status_id}>
                {s.status}
              </option>
            ))}
          </select>
          <small className="text-muted">
            Inactive status will prevent user login
          </small>
        </div>

        {isPrimaryTenant && tenantName && (
          <div className="col-md-12">
            <label className="form-label">Tenant</label>
            <input className="form-control" value={tenantName} readOnly />
          </div>
        )}

        <div className="col-md-6">
          <label className="form-label">
            Role <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            name="role_id"
            value={formData.role_id}
            onChange={handleChange}
            required
          >
            <option value="">Select Role</option>
            {roles.map((r) => (
              <option key={r.role_id} value={r.role_id}>
                {r.role}
              </option>
            ))}
          </select>
          <small className="text-muted">
            Role changes take effect immediately
          </small>
        </div>

        <div className="col-md-6">
          <label className="form-label">
            Phone <span className="text-danger">*</span>
          </label>
          <input
            name="phone_number"
            className="form-control"
            value={formData.phone_number}
            onChange={handleChange}
          />
        </div>

        {/* Payment Rates Section */}
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center py-2">
              <h6 className="mb-0">
                <i className="bi bi-currency-dollar me-2"></i>
                Payment Rates
              </h6>
              {isAdmin && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowRateHistory(!showRateHistory)}
                >
                  <i
                    className={`bi bi-${
                      showRateHistory ? "chevron-up" : "clock-history"
                    } me-1`}
                  ></i>
                  {showRateHistory ? "Hide History" : "View Rate History"}
                </button>
              )}
            </div>
            <div className="card-body">
              {/* Current Rate Inputs */}
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label">
                    Current Hourly Rate ($)
                    <i
                      className="bi bi-info-circle ms-1 text-muted"
                      title="This is the current rate. Use Rate History to schedule future changes or view past rates."
                      style={{ cursor: "help" }}
                    ></i>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="number"
                      className="form-control"
                      name="hourly_rate"
                      value={
                        formData.hourly_rate !== undefined
                          ? formData.hourly_rate
                          : 0
                      }
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                    />
                    <span className="input-group-text">/hour</span>
                  </div>
                  <small className="text-muted">
                    Rate applied to job log hours for payment
                  </small>
                </div>

                <div className="col-md-6">
                  <label className="form-label">
                    Current Mileage Rate ($/mile)
                    <i
                      className="bi bi-info-circle ms-1 text-muted"
                      title="This is the current rate. Use Rate History to schedule future changes or view past rates."
                      style={{ cursor: "help" }}
                    ></i>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="number"
                      className="form-control"
                      name="mileage_rate"
                      value={
                        formData.mileage_rate !== undefined
                          ? formData.mileage_rate
                          : 0
                      }
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                    />
                    <span className="input-group-text">/mile</span>
                  </div>
                  <small className="text-muted">
                    Rate applied to mileage logs for reimbursement
                  </small>
                </div>
              </div>

              {/* Rate History Section */}
              {showRateHistory && (
                <div className="rate-history-section">
                  <hr />

                  {/* Hourly Rate History */}
                  <div className="mb-4">
                    <UserRateHistory
                      userId={user.user_id}
                      userName={user.name}
                      rateType="hourly"
                    />
                  </div>

                  <hr />

                  {/* Mileage Rate History */}
                  <div>
                    <UserRateHistory
                      userId={user.user_id}
                      userName={user.name}
                      rateType="mileage"
                    />
                  </div>
                </div>
              )}

              {/* Help Text */}
              {!showRateHistory && isAdmin && (
                <div className="alert alert-info py-2 mt-3 mb-0 small">
                  <i className="bi bi-lightbulb me-1"></i>
                  <strong>Tip:</strong> Click "View Rate History" to see all
                  historical rates, schedule future rate changes, or backdate
                  corrections (when allowed).
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Placement Management Access */}
        <div className="col-12">
          <div className="card bg-light">
            <div className="card-body py-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="has_placement_access"
                  id="has_placement_access"
                  checked={formData.has_placement_access}
                  onChange={handleChange}
                />
                <label
                  className="form-check-label"
                  htmlFor="has_placement_access"
                >
                  <strong>
                    <i className="bi bi-briefcase me-2"></i>
                    Placement Management Access
                  </strong>
                  <br />
                  <small className="text-muted">
                    Grant access to Placement Management features including:
                  </small>
                  <ul className="mb-0 mt-1 small text-muted">
                    <li>CRP-30: Job Placement Plan</li>
                    <li>CRP-31: Placement Services Assessment</li>
                    <li>CRP-32: Job Placement Follow-Up</li>
                    <li>CRP-33: Job Search Data Sheet</li>
                    <li>Contacts, Outreach, Placements, and Retention features</li>
                  </ul>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <label className="form-label">
            Address Line 1 <span className="text-danger">*</span>
          </label>
          <input
            name="address_line_1"
            className="form-control"
            value={formData.address_line_1}
            onChange={handleChange}
            required
          />
        </div>

        <div className="col-12">
          <label className="form-label">Address Line 2</label>
          <input
            name="address_line_2"
            className="form-control"
            value={formData.address_line_2}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">
            City <span className="text-danger">*</span>
          </label>
          <input
            name="city"
            className="form-control"
            value={formData.city}
            onChange={handleChange}
            required
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">
            State <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            name="state_id"
            value={formData.state_id || ""}
            onChange={handleChange}
            required
          >
            <option value="">Select State</option>
            {states.map((s) => (
              <option key={s.state_id} value={s.state_id}>
                {s.state_code} - {s.state_name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label">
            ZIP <span className="text-danger">*</span>
          </label>
          <input
            name="postal_code"
            className="form-control"
            value={formData.postal_code}
            onChange={handleChange}
            required
          />
        </div>

        {/* Data Modification Acknowledgment */}
        <div className="col-12">
          <div className="card bg-light">
            <div className="card-body">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="dataChangeAcknowledgment"
                  checked={dataChangeAcknowledged}
                  onChange={(e) => setDataChangeAcknowledged(e.target.checked)}
                  required
                />
                <label
                  className="form-check-label"
                  htmlFor="dataChangeAcknowledgment"
                >
                  <strong>Data Modification Acknowledgment Required</strong>
                  <br />I confirm that:
                  <ul className="mt-2 mb-0">
                    <li>
                      I have a legitimate business need to modify this user's
                      information
                    </li>
                    <li>The changes I am making are accurate and necessary</li>
                    <li>
                      I understand all modifications are logged and auditable
                    </li>
                    <li>I am authorized to make these changes</li>
                  </ul>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Audit Trail Notice */}
        <div className="col-12">
          <div className="alert alert-info">
            <i className="bi bi-info-circle me-2"></i>
            <strong>Audit Trail:</strong> This modification will be recorded
            with your user ID ({currentUser?.email}), timestamp, and all changed
            fields. Previous values will be preserved in the audit log.
          </div>
        </div>

        <div className="col-12 d-flex justify-content-between">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onFinish}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="btn btn-success"
            disabled={!dataChangeAcknowledged}
          >
            <i className="bi bi-check-circle me-2"></i>
            Save Changes
          </button>
        </div>
      </form>

      {/* Footer Notice */}
      <div className="mt-4 text-muted small">
        <p>
          <i className="bi bi-shield-check me-1"></i>
          All data modifications are performed over encrypted connections and
          comply with HIPAA Security Rule requirements.
        </p>
      </div>
    </div>
  );
};

export default EditUserForm;